import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ShieldCheck } from "lucide-react";
import { PrimaryButton } from "./Buttons";
import { acceptLegal, getPendingLegal } from "../services/legal";

/**
 * Blocking Terms / Privacy acceptance.
 *
 * This modal is only the *surface* of the gate — the real one is a server-side dependency in
 * verify_access, which is what makes the acceptance evidence rather than a UI convention. That
 * matters because three authenticated routes in this app have no route guard at all, and any client
 * gate is bypassed by calling the API directly.
 *
 * Consequences of the server being authoritative:
 *   - nothing is remembered in localStorage; the answer is re-read from /legal/pending;
 *   - the modal is non-dismissable (no close control, no overlay click) — the only way past it is
 *     to accept, which is exactly what a blocked API would enforce anyway;
 *   - each document must be scrolled to the end before it can be ticked. A clickwrap where the
 *     text was never displayed is the first thing challenged in a dispute.
 */
function LegalGate() {
  const [pending, setPending] = useState(null);
  const [readDocs, setReadDocs] = useState({});
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const scrollRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await getPendingLegal();
      setPending(res?.isBlocked ? res : null);
    } catch {
      // A failure here must not lock anyone out of the app: the server-side gate is the real one,
      // so a request that genuinely needs acceptance will still be refused with its own message.
      setPending(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Short documents may not overflow at all — in that case there is nothing to scroll and the
  // "read it" condition is already satisfied. Without this the accept button never enables.
  const markReadIfNotScrollable = (documentId, el) => {
    if (el && el.scrollHeight <= el.clientHeight + 4) {
      setReadDocs((r) => (r[documentId] ? r : { ...r, [documentId]: true }));
    }
  };

  const onScroll = (documentId) => (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setReadDocs((r) => (r[documentId] ? r : { ...r, [documentId]: true }));
    }
  };

  const onAccept = async () => {
    setSaving(true);
    try {
      await acceptLegal(
        pending.documents.map((d) => ({
          documentId: d.documentId,
          // Echoed back so the server can refuse an acceptance of text that changed under us.
          documentSha256: d.contentSha256,
        })),
      );
      toast.success("Thank you — your acceptance has been recorded.");
      setPending(null);
    } catch (error) {
      const detail = error?.detail || error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not record your acceptance.");
      // A 409 means the text moved on; re-read so they see the current version.
      if (error?.response?.status === 409) {
        setReadDocs({});
        setAgreed(false);
        load();
      }
    } finally {
      setSaving(false);
    }
  };

  if (!pending?.documents?.length) return null;

  const allRead = pending.documents.every((d) => readDocs[d.documentId]);

  return (
    // No close control and no overlay click-through: this is non-dismissable by design.
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
          <ShieldCheck size={20} className="text-flamingo-600" />
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {pending.isReaccept ? "We've updated our terms" : "Before you get started"}
            </h2>
            <p className="text-xs text-slate-500">
              {pending.isReaccept
                ? "Please review and accept the updated documents to continue."
                : "Please read and accept the following to use BuildVizer."}
            </p>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {pending.documents.map((doc) => (
            <div key={doc.documentId} className="rounded-lg border border-slate-200">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
                <span className="text-sm font-semibold text-slate-800">{doc.title}</span>
                <span className="text-[11px] text-slate-400">
                  v{doc.version}
                  {readDocs[doc.documentId] ? " · read" : " · scroll to the end"}
                </span>
              </div>
              <div
                ref={(el) => {
                  scrollRefs.current[doc.documentId] = el;
                  markReadIfNotScrollable(doc.documentId, el);
                }}
                onScroll={onScroll(doc.documentId)}
                className="max-h-56 overflow-y-auto px-4 py-3 text-sm leading-relaxed text-slate-700"
                // The content is authored by BuildVizer and served from our own API — it is not
                // user input. It is rendered as HTML because the published documents are HTML.
                dangerouslySetInnerHTML={{ __html: doc.contentHtml }}
              />
            </div>
          ))}
        </div>

        <div className="space-y-3 border-t border-slate-200 px-6 py-4">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={agreed}
              disabled={!allRead}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-flamingo-600 disabled:opacity-40"
            />
            <span className={allRead ? "" : "text-slate-400"}>
              I have read and agree to the documents above.
              {!allRead && " (Scroll each one to the end first.)"}
            </span>
          </label>
          <PrimaryButton onClick={onAccept} disabled={!allRead || !agreed || saving}>
            {saving ? "Recording…" : `Accept and continue · ${new Date().toLocaleDateString("en-IN")}`}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default LegalGate;
